import dayjs from 'dayjs';

import type { DueExpense, ExpenseSummary } from '../api/cash';
import { formatAmount } from '../format/money';
import { dueWords } from './recurring';

/**
 * The Expenses board's figures (canvas "Cash & expense flow", board 2), web and phone: the periods
 * the register is read over, the band's sentence, and the bars by head and by account. Every total
 * is the server's (`GET /api/expense/summary`) — this only shapes and words it.
 */

export type SpendingPeriod = 'today' | 'week' | 'month' | 'all';

export const spendingPeriodLabels: Record<SpendingPeriod, string> = {
  today: 'Today',
  week: 'Week',
  month: 'Month',
  all: 'All',
};

/** The days a period covers, both included; `all` has no bounds. Weeks start on Monday. */
export function spendingRange(
  period: SpendingPeriod,
  today: string,
): { from: string | undefined; to: string | undefined } {
  const day = dayjs(today);

  switch (period) {
    case 'today':
      return { from: today, to: today };
    case 'week':
      // dayjs counts Sunday as 0; a shop's week runs Monday to Sunday.
      return { from: day.subtract((day.day() + 6) % 7, 'day').format('YYYY-MM-DD'), to: today };
    case 'month':
      return { from: day.startOf('month').format('YYYY-MM-DD'), to: today };
    case 'all':
      return { from: undefined, to: undefined };
  }
}

/** This month so far: the first to today. */
export function monthOf(today: string): { from: string; to: string; name: string } {
  return {
    from: dayjs(today).startOf('month').format('YYYY-MM-DD'),
    to: today,
    name: dayjs(today).format('MMMM'),
  };
}

/**
 * "₹38,450 spent in October across 42 vouchers. Shop rent is due in 3 days." — the second sentence
 * only when something is due, the most pressing first as the due list sorts it.
 */
export function spendingSentence(
  month: ExpenseSummary | undefined,
  monthName: string,
  due: readonly DueExpense[],
  today: string,
  currency = '',
): string {
  const first =
    month === undefined
      ? ''
      : month.count === 0
        ? `Nothing spent in ${monthName} yet.`
        : `${currency}${formatAmount(month.total)} spent in ${monthName} across ${month.count} ${
            month.count === 1 ? 'voucher' : 'vouchers'
          }.`;

  const next = due[0];
  const words = next ? dueWords(next.dueOn, today).text : '';
  const second = next ? `${next.name} is ${words.charAt(0).toLowerCase()}${words.slice(1)}.` : '';

  return [first, second].filter(Boolean).join(' ');
}

/** The heaviest head: its name, amount and share of the total, or null with nothing spent. */
export function biggestHead(
  summary: ExpenseSummary | undefined,
): { name: string; amount: number; share: number } | null {
  const top = summary?.byHead[0];

  if (!summary || !top || summary.total <= 0) {
    return null;
  }

  return {
    name: top.headName,
    amount: top.amount,
    share: Math.round((top.amount / summary.total) * 100),
  };
}

export interface SpendingBar {
  key: number;
  label: string;
  amount: number;
  /** Width as a percentage, 2–100. */
  width: number;
}

/**
 * The by-head bars, each against the heaviest head, so the largest fills the track and a small head
 * still shows a sliver. At most `limit`; the rest folded into "Other heads".
 */
export function headBars(summary: ExpenseSummary | undefined, limit = 6): SpendingBar[] {
  const heads = summary?.byHead ?? [];
  const shown = heads.slice(0, heads.length > limit ? limit - 1 : limit);
  const rest = heads.slice(shown.length);
  const rows = [
    ...shown.map((head) => ({
      key: head.expenseHeadId,
      label: head.headName,
      amount: head.amount,
    })),
    ...(rest.length > 0
      ? [
          {
            key: -1,
            label: 'Other heads',
            amount: rest.reduce((sum, head) => sum + head.amount, 0),
          },
        ]
      : []),
  ];
  const most = Math.max(0, ...rows.map((row) => row.amount));

  return rows.map((row) => ({
    ...row,
    width: most > 0 ? Math.max(2, Math.round((row.amount / most) * 100)) : 0,
  }));
}

/** The paid-from strip: each account's share of what left, in percent of the whole. */
export function accountShares(
  summary: ExpenseSummary | undefined,
): (ExpenseSummary['byAccount'][number] & { width: number })[] {
  const total = (summary?.byAccount ?? []).reduce((sum, account) => sum + account.amount, 0);

  return (summary?.byAccount ?? []).map((account) => ({
    ...account,
    width: total > 0 ? Math.max(1, Math.round((account.amount / total) * 100)) : 0,
  }));
}
